/**
 * NotificationCenter — Unified notification dropdown in the header.
 *
 * Replaces separate NotificationBell + ProcessingPill + toast notifications.
 * Single icon with badge count, dropdown shows all notification types.
 *
 * Sections:
 * 1. Active Jobs (processing) — with progress indicator
 * 2. Recent Notifications (local ephemeral) — auto-dismiss but shown while open
 * 3. Completed Notifications (Firestore) — persist until marked read
 *
 * See: docs/features/infrastructure/notification-system.md
 */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { Bell, X, CheckCircle, XCircle, AlertTriangle, Info, Loader, Share2 } from "lucide-react";
import { useUnifiedNotifications, NOTIF_TYPE } from "../../contexts/UnifiedNotificationContext";
import { getData } from "../../utils/firestoreService";
import "../../css/NotificationCenter.css";

// Icons for notification types
const typeIcons = {
  [NOTIF_TYPE.SUCCESS]: <CheckCircle size={16} className="notif-icon notif-icon-success" />,
  [NOTIF_TYPE.ERROR]: <XCircle size={16} className="notif-icon notif-icon-error" />,
  [NOTIF_TYPE.WARNING]: <AlertTriangle size={16} className="notif-icon notif-icon-warning" />,
  [NOTIF_TYPE.INFO]: <Info size={16} className="notif-icon notif-icon-info" />,
  processing_complete: <CheckCircle size={16} className="notif-icon notif-icon-success" />,
  processing_failed: <XCircle size={16} className="notif-icon notif-icon-error" />,
  share_received: <Share2 size={16} className="notif-icon notif-icon-info" />,
};

function formatRelativeTime(timestamp) {
  if (!timestamp) return "";
  const date = typeof timestamp === "number" ? new Date(timestamp) : (timestamp.toDate ? timestamp.toDate() : new Date(timestamp));
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export default function NotificationCenter({ onNavigateToDocument, onNavigateToPatient }) {
  const {
    activeJobs,
    localNotifications,
    firestoreNotifications,
    unreadCount,
    dismissNotification,
    dismissAllLocal,
    dismissFirestoreNotification,
    markAllFirestoreRead,
  } = useUnifiedNotifications();

  const [anchorEl, setAnchorEl] = useState(null);
  const [patientNamesById, setPatientNamesById] = useState({});
  const isOpen = Boolean(anchorEl);

  const notificationPatientIds = useMemo(() => {
    const ids = new Set();
    activeJobs.forEach((job) => {
      if (job.patientId) ids.add(job.patientId);
    });
    firestoreNotifications.forEach((notif) => {
      if (notif.patientId) ids.add(notif.patientId);
    });
    return Array.from(ids);
  }, [activeJobs, firestoreNotifications]);

  useEffect(() => {
    let cancelled = false;
    const missingIds = notificationPatientIds.filter((id) => !patientNamesById[id]);
    if (missingIds.length === 0) return () => { };

    const loadPatientNames = async () => {
      const resolved = await Promise.all(
        missingIds.map(async (patientId) => {
          try {
            const patient = await getData(`patients/${patientId}`);
            const liveName = [patient?.firstName, patient?.lastName].filter(Boolean).join(" ").trim()
              || patient?.name
              || patient?.patientName
              || "";
            return [patientId, liveName];
          } catch (_error) {
            return [patientId, ""];
          }
        })
      );

      if (cancelled) return;

      setPatientNamesById((prev) => {
        const next = { ...prev };
        resolved.forEach(([patientId, liveName]) => {
          if (liveName) next[patientId] = liveName;
        });
        return next;
      });
    };

    loadPatientNames();

    return () => {
      cancelled = true;
    };
  }, [notificationPatientIds, patientNamesById]);

  const resolvePatientName = useCallback((item) => {
    if (!item?.patientId) return item?.patientName || "";
    return patientNamesById[item.patientId] || item.patientName || "";
  }, [patientNamesById]);

  // Calculate total badge count
  const totalCount = activeJobs.length + localNotifications.length + unreadCount;

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNavigate = useCallback((patientId, docId) => {
    handleClose();
    onNavigateToDocument?.(patientId, docId);
  }, [handleClose, onNavigateToDocument]);

  const handleNavigatePatient = useCallback((patientId) => {
    handleClose();
    onNavigateToPatient?.(patientId);
  }, [handleClose, onNavigateToPatient]);

  const handleDismissLocal = useCallback((id) => {
    dismissNotification(id);
  }, [dismissNotification]);

  const handleDismissFirestore = useCallback((id, patientId, docId, type) => {
    dismissFirestoreNotification(id);
    if (type === 'share_received' && patientId) {
      handleNavigatePatient(patientId);
      return;
    }
    if (patientId && docId) {
      handleNavigate(patientId, docId);
    }
  }, [dismissFirestoreNotification, handleNavigate, handleNavigatePatient]);

  const handleMarkAllRead = useCallback(() => {
    dismissAllLocal();
    markAllFirestoreRead();
  }, [dismissAllLocal, markAllFirestoreRead]);

  // Build progress label for active job
  const getJobProgress = (job) => {
    if (job.isPending) return "Submitting…";
    if (job.progressPercent > 0) return `${job.progressPercent}%`;
    return "Starting…";
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        aria-label={`${totalCount} notifications`}
        sx={{ color: "inherit", padding: 0 }}
      >
        <Badge
          variant="dot"
          invisible={totalCount === 0 || activeJobs.length > 0}
          sx={{
            "& .MuiBadge-dot": {
              width: 8,
              height: 8,
              minWidth: 8,
              backgroundColor: "#dd9b6e",
              top: 2,
              right: 3,
            },
          }}
        >
          {activeJobs.length > 0 ? (
            <span className="notification-center-bell-wrapper">
              <Bell className="app-shell-header-icon" size={25} />
              <span className="notification-center-active-dot" />
            </span>
          ) : (
            <Bell className="app-shell-header-icon" size={25} />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { mt: 1, borderRadius: 2, maxHeight: 480, overflow: "hidden" },
          },
        }}
      >
        <Box className="notification-center-panel">
          {/* Header */}
          <Box className="notification-center-header">
            <Typography variant="subtitle2">Notifications</Typography>
            {totalCount > 0 && (
              <Button size="small" onClick={handleMarkAllRead}>
                Clear all
              </Button>
            )}
          </Box>

          <Divider />

          {/* Empty state */}
          {totalCount === 0 && (
            <Box className="notification-center-empty">
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          )}

          {/* Active Jobs Section */}
          {activeJobs.length > 0 && (
            <Box className="notification-center-section">
              <Typography variant="caption" className="notification-center-section-title">
                Processing
              </Typography>
              {activeJobs.map((job) => (
                <Box key={job.id} className="notification-center-item notification-center-job">
                  <div className="notification-center-job-indicator">
                    <Loader size={14} className="notification-center-spinner" />
                  </div>
                  <div className="notification-center-item-content">
                    <span className="notification-center-item-message">
                      {job.statusLabel || `Processing ${job.sourceType || "Recording"}`}
                    </span>
                    <span className="notification-center-item-meta">
                      {resolvePatientName(job) && `${resolvePatientName(job)} · `}
                      {getJobProgress(job)}
                    </span>
                  </div>
                </Box>
              ))}
            </Box>
          )}

          {/* Local Notifications Section */}
          {localNotifications.length > 0 && (
            <Box className="notification-center-section">
              <Typography variant="caption" className="notification-center-section-title">
                Recent
              </Typography>
              {localNotifications.map((notif) => (
                <Box
                  key={notif.id}
                  className={`notification-center-item notification-center-${notif.type}`}
                  onClick={() => {
                    if (notif.onClick) notif.onClick();
                    handleDismissLocal(notif.id);
                  }}
                >
                  <div className="notification-center-item-icon">
                    {typeIcons[notif.type] || typeIcons[NOTIF_TYPE.INFO]}
                  </div>
                  <div className="notification-center-item-content">
                    <span className="notification-center-item-message">{notif.message}</span>
                    <span className="notification-center-item-meta">
                      {formatRelativeTime(notif.createdAt)}
                    </span>
                  </div>
                  <button
                    className="notification-center-item-dismiss"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismissLocal(notif.id);
                    }}
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </Box>
              ))}
            </Box>
          )}

          {/* Firestore Notifications Section */}
          {firestoreNotifications.length > 0 && (
            <Box className="notification-center-section">
              <Typography variant="caption" className="notification-center-section-title">
                Completed
              </Typography>
              {firestoreNotifications.map((notif) => (
                <Box
                  key={notif.id}
                  className={`notification-center-item notification-center-firestore`}
                  onClick={() => handleDismissFirestore(notif.id, notif.patientId, notif.docId, notif.type)}
                >
                  <div className="notification-center-item-icon">
                    {typeIcons[notif.type] || typeIcons.processing_complete}
                  </div>
                  <div className="notification-center-item-content">
                    <span className="notification-center-item-message">{notif.message}</span>
                    <span className="notification-center-item-meta">
                      {resolvePatientName(notif) && `${resolvePatientName(notif)} · `}
                      {formatRelativeTime(notif.createdAt)}
                    </span>
                  </div>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}
